import { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Loader2, Package, Pencil, Check, ArrowUpDown, Upload, ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  icon_url: string | null;
  created_at: string;
  sort_order?: number;
}

interface ProductCategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const AVAILABLE_ICONS = [
  { value: "smartphone", label: "Smartphone" },
  { value: "laptop", label: "Laptop" },
  { value: "tablet", label: "Tablet" },
  { value: "headphones", label: "Cuffie" },
  { value: "watch", label: "Orologio" },
  { value: "tv", label: "TV" },
  { value: "camera", label: "Fotocamera" },
  { value: "gamepad-2", label: "Gaming" },
  { value: "cpu", label: "Componenti" },
  { value: "hard-drive", label: "Storage" },
];

const ProductCategoryManager = ({ isOpen, onClose }: ProductCategoryManagerProps) => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState("smartphone");
  const [newIconFile, setNewIconFile] = useState<File | null>(null);
  const [newIconPreview, setNewIconPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<ProductCategory | null>(null);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editIconFile, setEditIconFile] = useState<File | null>(null);
  const [editIconPreview, setEditIconPreview] = useState<string | null>(null);
  const [movingCategory, setMovingCategory] = useState<ProductCategory | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const newIconInputRef = useRef<HTMLInputElement>(null);
  const editIconInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("product_categories")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching product categories:", error);
      toast.error("Errore nel caricamento delle categorie prodotti");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const uploadIcon = async (file: File, categorySlug: string): Promise<string | null> => {
    const ext = file.name.split(".").pop();
    const path = `${categorySlug}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("category-icons").upload(path, file, { upsert: true });
    if (error) {
      console.error("Upload error:", error);
      return null;
    }
    const { data: urlData } = supabase.storage.from("category-icons").getPublicUrl(path);
    return urlData.publicUrl;
  };

  const handleNewIconSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewIconFile(file);
    setNewIconPreview(URL.createObjectURL(file));
  };

  const handleEditIconSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditIconFile(file);
    setEditIconPreview(URL.createObjectURL(file));
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Inserisci un nome per la categoria");
      return;
    }

    setIsSaving(true);
    try {
      const slug = generateSlug(newCategoryName);
      const maxOrder = Math.max(...categories.map(c => c.sort_order || 0), 0);
      
      let iconUrl: string | null = null;
      if (newIconFile) {
        iconUrl = await uploadIcon(newIconFile, slug);
      }

      const { error } = await supabase.from("product_categories").insert({
        name: newCategoryName.trim(),
        slug,
        icon: newCategoryIcon,
        icon_url: iconUrl,
        sort_order: maxOrder + 1,
      });

      if (error) throw error;

      toast.success("Categoria prodotti creata!");
      setNewCategoryName("");
      setNewCategoryIcon("smartphone");
      setNewIconFile(null);
      setNewIconPreview(null);
      fetchCategories();
    } catch (error) {
      console.error("Error creating product category:", error);
      toast.error("Errore nella creazione della categoria");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;

    try {
      await supabase
        .from("products")
        .update({ category_id: null })
        .eq("category_id", deleteCategory.id);

      const { error } = await supabase
        .from("product_categories")
        .delete()
        .eq("id", deleteCategory.id);

      if (error) throw error;

      toast.success("Categoria eliminata!");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting product category:", error);
      toast.error("Errore nell'eliminazione della categoria");
    } finally {
      setDeleteCategory(null);
    }
  };

  const handleStartEdit = (category: ProductCategory) => {
    setEditingCategory(category);
    setEditName(category.name);
    setEditIcon(category.icon || "smartphone");
    setEditIconFile(null);
    setEditIconPreview(category.icon_url || null);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editName.trim()) return;

    try {
      let iconUrl = editingCategory.icon_url;
      if (editIconFile) {
        iconUrl = await uploadIcon(editIconFile, generateSlug(editName));
      }

      const { error } = await supabase
        .from("product_categories")
        .update({
          name: editName.trim(),
          slug: generateSlug(editName),
          icon: editIcon,
          icon_url: iconUrl,
        })
        .eq("id", editingCategory.id);

      if (error) throw error;

      toast.success("Categoria aggiornata!");
      setEditingCategory(null);
      setEditName("");
      setEditIcon("");
      setEditIconFile(null);
      setEditIconPreview(null);
      fetchCategories();
    } catch (error) {
      console.error("Error updating product category:", error);
      toast.error("Errore nell'aggiornamento della categoria");
    }
  };

  const handleRemoveIconUrl = async (category: ProductCategory) => {
    try {
      const { error } = await supabase
        .from("product_categories")
        .update({ icon_url: null })
        .eq("id", category.id);
      if (error) throw error;
      toast.success("Immagine icona rimossa");
      fetchCategories();
    } catch (error) {
      toast.error("Errore nella rimozione dell'immagine");
    }
  };

  const handleStartMove = (category: ProductCategory) => {
    setMovingCategory(category);
    setSelectedPosition("");
  };

  const handleConfirmMove = async () => {
    if (!movingCategory || !selectedPosition) return;

    const targetIndex = parseInt(selectedPosition);
    const currentIndex = categories.findIndex(c => c.id === movingCategory.id);

    if (targetIndex === currentIndex) {
      setMovingCategory(null);
      return;
    }

    try {
      const newCategories = [...categories];
      const [moved] = newCategories.splice(currentIndex, 1);
      newCategories.splice(targetIndex, 0, moved);

      for (let i = 0; i < newCategories.length; i++) {
        await supabase
          .from("product_categories")
          .update({ sort_order: i })
          .eq("id", newCategories[i].id);
      }

      toast.success("Posizione aggiornata!");
      fetchCategories();
    } catch (error) {
      console.error("Error moving category:", error);
      toast.error("Errore nello spostamento della categoria");
    } finally {
      setMovingCategory(null);
    }
  };

  const getPositionOptions = () => {
    return categories.map((c, index) => ({
      value: index.toString(),
      label: `${index + 1}. ${c.name}`,
      isCurrent: movingCategory?.id === c.id
    }));
  };

  const renderCategoryItem = (category: ProductCategory, index: number) => {
    const isEditing = editingCategory?.id === category.id;

    return (
      <motion.div
        key={category.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-colors group"
      >
        <div className="flex items-center gap-2 flex-1">
          {/* Icon preview */}
          {category.icon_url ? (
            <img src={category.icon_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <Package className="w-4 h-4 text-primary" />
          )}
          
          {isEditing ? (
            <div className="flex flex-col gap-2 flex-1">
              <div className="flex items-center gap-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveEdit();
                    if (e.key === "Escape") setEditingCategory(null);
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={editIcon} onValueChange={setEditIcon}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_ICONS.map(icon => (
                      <SelectItem key={icon.value} value={icon.value}>
                        {icon.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  ref={editIconInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleEditIconSelect}
                />
                <button
                  onClick={() => editIconInputRef.current?.click()}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 text-primary text-xs hover:bg-primary/20 transition-colors"
                >
                  <Upload className="w-3 h-3" />
                  Immagine
                </button>
                {editIconPreview && (
                  <img src={editIconPreview} alt="" className="w-8 h-8 rounded-lg object-cover border border-border" />
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleSaveEdit}
                  className="p-1.5 rounded-full text-green-500 hover:bg-green-500/10 transition-all"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditingCategory(null); setEditIconFile(null); setEditIconPreview(null); }}
                  className="p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <span className="font-medium">{category.name}</span>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {category.icon_url ? "immagine" : (category.icon || "smartphone")}
              </span>
            </>
          )}
        </div>
        
        {!isEditing && (
          <div className="flex items-center gap-1">
            {category.icon_url && (
              <button
                onClick={() => handleRemoveIconUrl(category)}
                className="p-1.5 rounded-full text-orange-500 hover:bg-orange-500/10 transition-all"
                title="Rimuovi immagine icona"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => handleStartMove(category)}
              className="p-1.5 rounded-full text-blue-500 hover:bg-blue-500/10 transition-all"
              title="Sposta"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleStartEdit(category)}
              className="p-1.5 rounded-full text-primary hover:bg-primary/10 transition-all"
              title="Modifica"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeleteCategory(category)}
              className="p-1.5 rounded-full text-destructive hover:bg-destructive/10 transition-all"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
        <DrawerContent className="h-[85vh] bg-background border-t border-border">
          <DrawerHeader className="relative border-b border-border">
            <button
              onClick={onClose}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">
              Categorie Prodotti
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Add New Category */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-2xl">
              <h3 className="text-sm font-medium text-foreground">
                Nuova Categoria Prodotti
              </h3>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Nome categoria"
              />
              <Select value={newCategoryIcon} onValueChange={setNewCategoryIcon}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Seleziona icona predefinita" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border">
                  {AVAILABLE_ICONS.map(icon => (
                    <SelectItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Image icon upload */}
              <div className="flex items-center gap-3">
                <input
                  ref={newIconInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleNewIconSelect}
                />
                <button
                  onClick={() => newIconInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-border bg-background text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Carica immagine icona (opzionale)
                </button>
                {newIconPreview && (
                  <div className="relative">
                    <img src={newIconPreview} alt="" className="w-10 h-10 rounded-lg object-cover border border-border" />
                    <button
                      onClick={() => { setNewIconFile(null); setNewIconPreview(null); }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Se carichi un'immagine, verrà usata al posto dell'icona predefinita.
              </p>

              <Button
                onClick={handleAddCategory}
                disabled={isSaving}
                className="w-full gap-2"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Aggiungi Categoria
              </Button>
            </div>

            {/* Categories List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground px-1">
                Categorie esistenti
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna categoria creata</p>
                </div>
              ) : (
                <AnimatePresence>
                  {categories.map((category, index) => renderCategoryItem(category, index))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{deleteCategory?.name}". 
              I prodotti associati non saranno più categorizzati.
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Position Dialog */}
      <AlertDialog open={!!movingCategory} onOpenChange={() => setMovingCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Sposta "{movingCategory?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona la nuova posizione per questa categoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona posizione" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border">
                {getPositionOptions().map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={option.isCurrent ? "font-bold text-primary" : ""}
                  >
                    {option.label} {option.isCurrent && "(attuale)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMove}
              disabled={!selectedPosition}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Sposta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProductCategoryManager;